/**
 * MEL Documents Page — Admin tool
 * Upload, view, and manage MEL documents per aircraft type.
 */

import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '@/services/authService';
import { API_BASE_URL } from '@/services/api';
import {
  DocumentArrowUpIcon,
  DocumentTextIcon,
  TrashIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface MelDocument {
  id: number;
  aircraftType: string;
  version: string | null;
  filePath: string;
  parsedAt: string | null;
  itemCount: number;
  parseError: string | null;
  createdAt: string;
}

export default function MelDocumentsPage() {
  const [documents, setDocuments] = useState<MelDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Upload form
  const [file, setFile] = useState<File | null>(null);
  const [aircraftType, setAircraftType] = useState('');
  const [version, setVersion] = useState('');

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/mel/documents`);
      if (res.ok) {
        const data = await res.json();
        setDocuments(data.data || []);
      }
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleUpload = async () => {
    if (!file || !aircraftType.trim()) return;

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('aircraftType', aircraftType.trim());
      if (version.trim()) formData.append('version', version.trim());

      const res = await authFetch(`${API_BASE_URL}/mel/documents`, {
        method: 'POST',
        body: formData,
        headers: {},
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || data.error || 'Upload failed');
      }

      setSuccess(`MEL document uploaded for ${aircraftType}. Parsing will begin automatically.`);
      setFile(null);
      setAircraftType('');
      setVersion('');
      fetchDocuments();
    } catch (err: any) {
      setError(err.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleReparse = async (id: number) => {
    setParsing(id);
    setError('');
    setSuccess('');

    try {
      const res = await authFetch(`${API_BASE_URL}/mel/documents/${id}/parse`, {
        method: 'POST',
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Re-parse failed');
      }

      setSuccess('Document re-parsed successfully.');
      fetchDocuments();
    } catch (err: any) {
      setError(err.message || 'Failed to re-parse document');
    } finally {
      setParsing(null);
    }
  };

  const handleDelete = async (id: number, aircraftType: string) => {
    if (!confirm(`Delete MEL document for ${aircraftType}? This removes all parsed items.`)) return;

    setError('');
    setSuccess('');

    try {
      const res = await authFetch(`${API_BASE_URL}/mel/documents/${id}`, {
        method: 'DELETE',
      });

      if (!res.ok) throw new Error('Delete failed');

      setSuccess(`MEL document for ${aircraftType} deleted.`);
      fetchDocuments();
    } catch (err: any) {
      setError(err.message || 'Failed to delete document');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
            <DocumentTextIcon className="w-7 h-7 text-primary" />
            MEL Documents
          </h1>
          <p className="text-muted-foreground mt-1">
            Upload and manage Minimum Equipment List PDFs by aircraft type
          </p>
        </div>

        {/* Status messages */}
        {error && (
          <div className="mb-6 p-3 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 rounded-lg text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 p-3 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 rounded-lg text-sm">
            {success}
          </div>
        )}

        {/* Upload card */}
        <div className="bg-card border border-border rounded-xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <DocumentArrowUpIcon className="w-5 h-5 text-muted-foreground" />
            Upload MEL Document
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Aircraft Type <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={aircraftType}
                onChange={(e) => setAircraftType(e.target.value)}
                placeholder="e.g. CE-560XL"
                className="w-full h-11 px-3 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Version (optional)
              </label>
              <input
                type="text"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="e.g. Rev 12"
                className="w-full h-11 px-3 rounded-md border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-foreground mb-1.5">
              MEL PDF <span className="text-red-500">*</span>
            </label>
            <label className="block border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-all">
              <DocumentArrowUpIcon className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-foreground font-medium">
                {file ? file.name : 'Click or drop MEL PDF here'}
              </p>
              {file && (
                <p className="text-xs text-muted-foreground mt-1">
                  {(file.size / (1024 * 1024)).toFixed(1)} MB
                </p>
              )}
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
              />
            </label>
          </div>

          <button
            onClick={handleUpload}
            disabled={!file || !aircraftType.trim() || uploading}
            className="w-full py-3 bg-primary text-primary-foreground font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                Uploading & parsing...
              </>
            ) : (
              <>
                <DocumentArrowUpIcon className="w-5 h-5" />
                Upload & Parse
              </>
            )}
          </button>
        </div>

        {/* Documents list */}
        <h2 className="text-lg font-semibold text-foreground mb-4">Loaded Documents</h2>

        {loading ? (
          <div className="text-sm text-muted-foreground">Loading...</div>
        ) : documents.length === 0 ? (
          <div className="text-sm text-muted-foreground bg-card border border-border rounded-lg px-5 py-8 text-center">
            No MEL documents uploaded yet. Upload one above to enable MEL search.
          </div>
        ) : (
          <div className="space-y-3">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="bg-card border border-border rounded-lg px-5 py-4 flex items-center justify-between"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-foreground">{doc.aircraftType}</span>
                    {doc.version && (
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                        {doc.version}
                      </span>
                    )}
                    {doc.parsedAt ? (
                      <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                        <CheckCircleIcon className="w-3.5 h-3.5" />
                        {doc.itemCount} items parsed
                      </span>
                    ) : doc.parseError ? (
                      <span className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                        <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                        Parse error
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Not parsed</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Uploaded {new Date(doc.createdAt).toLocaleDateString()}
                    {doc.parsedAt && ` · Parsed ${new Date(doc.parsedAt).toLocaleDateString()}`}
                  </p>
                  {doc.parseError && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1 truncate max-w-md" title={doc.parseError}>
                      {doc.parseError}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleReparse(doc.id)}
                    disabled={parsing === doc.id}
                    className="p-2 text-muted-foreground hover:text-primary hover:bg-muted rounded-lg transition-colors disabled:opacity-50"
                    title="Re-parse document"
                  >
                    <ArrowPathIcon className={`w-4 h-4 ${parsing === doc.id ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={() => handleDelete(doc.id, doc.aircraftType)}
                    className="p-2 text-muted-foreground hover:text-red-500 hover:bg-muted rounded-lg transition-colors"
                    title="Delete document"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
